import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Euro, TrendingUp, Clock, Filter, Download, Calendar, User, Smartphone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinancialManagement = () => {
  const [summary, setSummary] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    client_name: '',
    device_type: '',
    paid_only: null
  });

  useEffect(() => {
    fetchSummary();
    fetchDetails();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/finances/summary`);
      setSummary(response.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e përmbledhjes financiare');
      console.error('Fetch summary error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async () => {
    setDetailsLoading(true);
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API}/admin/finances/details?${params}`);
      setDetailedData(response.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e detajeve financiare');
      console.error('Fetch details error:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchDetails();
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      client_name: '',
      device_type: '',
      paid_only: null
    });
    // Fetch details will be called automatically when filters change
    setTimeout(fetchDetails, 100);
  };

  const exportData = () => {
    if (!detailedData || !detailedData.tickets) {
      toast.error('Nuk ka të dhëna për eksport');
      return;
    }

    // Create CSV content
    const headers = ['Numri Tiketit', 'Klienti', 'Telefoni', 'Paisja', 'Marka', 'Çmimi (€)', 'I Paguar', 'Statusi', 'Data Regjistrimit'];
    const csvContent = [
      headers.join(','),
      ...detailedData.tickets.map(ticket => [
        ticket.ticket_number,
        `"${ticket.client_emri}"`,
        ticket.client_telefoni,
        ticket.paisja || '',
        ticket.marka || '',
        ticket.cmimi,
        ticket.paguar ? 'Po' : 'Jo',
        ticket.statusi,
        new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')
      ].join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `financat_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Të dhënat u eksportuan me sukses');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL') + ' në ' + date.toLocaleTimeString('sq-AL');
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-2 text-gray-600">Duke ngarkuar të dhënat financiare...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-500 text-white mr-4">
                  <Euro className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">{summary.total_revenue}€</p>
                  <p className="text-sm text-green-600">Totali i të ardhurave</p>
                  <p className="text-xs text-green-500">{summary.total_paid_tickets} tiketa të paguara</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-500 text-white mr-4">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-800">{summary.today_revenue}€</p>
                  <p className="text-sm text-blue-600">Sot</p>
                  <p className="text-xs text-blue-500">Sot</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-500 text-white mr-4">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-800">{summary.month_revenue}€</p>
                  <p className="text-sm text-purple-600">Këtë muaj</p>
                  <p className="text-xs text-purple-500">Nga fillimi i muajit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-yellow-500 text-white mr-4">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-800">{summary.pending_revenue}€</p>
                  <p className="text-sm text-yellow-600">Në pritje</p>
                  <p className="text-xs text-yellow-500">{summary.pending_tickets} tiketa</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filtrat
          </CardTitle>
          <CardDescription>Filtroni të dhënat financiare sipas kritereve</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="start_date">Data e fillimit</Label>
              <Input
                id="start_date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="end_date">Data e mbarimit</Label>
              <Input
                id="end_date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="client_name">Emri i klientit</Label>
              <Input
                id="client_name"
                placeholder="Kërko sipas emrit..."
                value={filters.client_name}
                onChange={(e) => handleFilterChange('client_name', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="device_type">Paisja</Label>
              <Input
                id="device_type"
                placeholder="Laptop, Desktop..."
                value={filters.device_type}
                onChange={(e) => handleFilterChange('device_type', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="paid_status">Statusi i pagesës</Label>
              <Select value={filters.paid_only?.toString() || 'all'} onValueChange={(value) => 
                handleFilterChange('paid_only', value === 'all' ? null : value === 'true')
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Të gjitha</SelectItem>
                  <SelectItem value="true">Të paguara</SelectItem>
                  <SelectItem value="false">Të papaguara</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex space-x-3 mt-4">
            <Button onClick={applyFilters} disabled={detailsLoading}>
              {detailsLoading ? 'Duke aplikuar...' : 'Apliko filtrat'}
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Pastro filtrat
            </Button>
            <Button variant="outline" onClick={exportData} disabled={!detailedData}>
              <Download className="w-4 h-4 mr-2" />
              Eksporto CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Financial Data */}
      <Card>
        <CardHeader>
          <CardTitle>Detajet e Transaksioneve</CardTitle>
          {detailedData?.summary && (
            <CardDescription>
              {detailedData.summary.total_count} tiketa - {detailedData.summary.paid_amount}€ të paguara, {detailedData.summary.pending_amount}€ në pritje
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {detailsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-gray-600">Duke ngarkuar detajet...</p>
            </div>
          ) : detailedData?.tickets?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nuk ka të dhëna financiare sipas filtrave</p>
          ) : (
            <div className="space-y-4">
              {detailedData?.tickets?.map((ticket) => (
                <div key={ticket.id} className="border rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          {ticket.ticket_number}
                        </Badge>
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <span className="font-medium">{ticket.client_emri}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Smartphone className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-600">{ticket.client_telefoni}</span>
                        </div>
                      </div>
                      
                      {ticket.paisja && (
                        <p className="text-sm text-slate-600 mb-1">
                          Paisja: {ticket.paisja} {ticket.marka}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-slate-500">
                        <span>Regjistruar: {formatDateTime(ticket.data_regjistrimit)}</span>
                        {ticket.data_perfundimit && (
                          <span>Përfunduar: {formatDateTime(ticket.data_perfundimit)}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center space-x-2 mb-2">
                        <Euro className="w-4 h-4 text-green-600" />
                        <span className="text-lg font-bold text-green-600">{ticket.cmimi}€</span>
                      </div>
                      <Badge className={ticket.paguar ? "bg-green-500" : "bg-red-500"}>
                        {ticket.paguar ? "I paguar" : "I papaguar"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialManagement;